import axiosInstance from './axiosConfig';

export interface EvaluationBatch {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  total_evaluations: number;
  completed_evaluations: number;
  failed_evaluations: number;
  progress_percentage: number;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

export interface EvaluationItem {
  id: string;
  batch_id: string;
  matter_id: string;
  matter_name: string;
  ckeditor_document_id: string;
  gold_standard_id: string;
  status: string;
  weighted_score?: number;
  rubric_score?: number; // Kept for backward compatibility - now contains weighted score
  gold_standard_score?: number;
  final_score?: number;
  started_at?: string;
  completed_at?: string;
  grading_time_seconds?: number;
  grading_result?: any; // Full structured grading result
  error_message?: string;
}

export interface DocumentSelection {
  document_id: string;
  document_name: string;
}

export interface MatterEvaluation {
  matter_id: string;
  matter_name: string;
  gold_standard_id: string;
  document_selections: DocumentSelection[];
}

export interface CreateEvaluationBatchParams {
  name: string;
  description?: string;
  matter_evaluations: MatterEvaluation[];
  options?: Record<string, any>;
}

export const createEvaluationBatch = async (
  params: CreateEvaluationBatchParams,
): Promise<EvaluationBatch> => {
  const response = await axiosInstance.post('/api/evaluation-batches/', params);
  return response.data;
};

export const getEvaluationBatches = async (
  status?: string,
): Promise<{
  batches: EvaluationBatch[];
  total: number;
}> => {
  const params = status ? `?status=${status}` : '';
  const response = await axiosInstance.get(`/api/evaluation-batches/${params}`);
  // ViewSet returns {batches: [...]} format, but we need to handle both old and new formats
  return {
    batches: response.data.batches || response.data || [],
    total:
      response.data.total ||
      (response.data.batches || response.data || []).length,
  };
};

export const getEvaluationBatch = async (
  batchId: string,
): Promise<{
  batch: EvaluationBatch;
  items: EvaluationItem[];
  summary_stats: Record<string, any>;
}> => {
  const response = await axiosInstance.get(
    `/api/evaluation-batches/${batchId}/`,
  );
  return response.data;
};

export const startEvaluation = async (
  batchId: string,
  itemIds?: string[],
): Promise<void> => {
  await axiosInstance.post('/api/evaluation-batches/start/', {
    batch_id: batchId,
    item_ids: itemIds,
  });
};

export const stopEvaluation = async (batchId: string): Promise<void> => {
  await axiosInstance.post(`/api/evaluation-batches/${batchId}/stop/`);
};

export const restartEvaluation = async (
  batchId: string,
): Promise<{
  message: string;
  batch_id: string;
  total_items: number;
  workflow_id: string;
  status: string;
}> => {
  const response = await axiosInstance.post(
    `/api/evaluation-batches/${batchId}/restart/`,
  );
  return response.data;
};
